import { PurchaseItem } from '../types/purchase';

export interface PurchaseUnitPriceFixUpdate {
  id: string;
  unitPrice: number;
  total: number;
  sourceItemId?: string;
  sourceItemName?: string;
}

export interface ExternalUnitPriceSource {
  unitPrice: number;
  sourceItemId?: string;
  sourceItemName?: string;
}

export type PurchaseUnitPriceUnmatchedReason = 'missing_name' | 'no_price_source';

export interface PurchaseUnitPriceUnmatchedRow {
  name: string;
  reason: PurchaseUnitPriceUnmatchedReason;
  count: number;
}

export interface PurchaseUnitPriceFixResult {
  totalDocs: number;
  targetDocs: number;
  updatedDocs: number;
  unmatchedDocs: number;
  updates: PurchaseUnitPriceFixUpdate[];
  topMatches: Array<{ name: string; unitPrice: number; count: number }>;
  unmatchedReasons: PurchaseUnitPriceUnmatchedRow[];
}

const isExternalSource = (
  value: ExternalUnitPriceSource | PurchaseItem,
): value is ExternalUnitPriceSource => {
  return 'sourceItemId' in value || 'sourceItemName' in value;
};

const normalizeNameForMatch = (value?: string): string => {
  return (value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const stripMonthToken = (value?: string): string => {
  return (value || '')
    .replace(/\b(t|th|thang)\s*([1-9]|1[0-2])\b/gi, ' ')
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const cleanupDisplayName = (value?: string): string => {
  return (value || '')
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const buildMatchKey = (name?: string): string => {
  const withoutMonth = stripMonthToken(name || '');
  const cleaned = cleanupDisplayName(withoutMonth || name || '');
  return normalizeNameForMatch(cleaned);
};

const roundNumber = (value: number, precision = 6): number => {
  if (!Number.isFinite(value)) return 0;
  const factor = 10 ** precision;
  return Math.round(value * factor) / factor;
};

const safeDateValue = (value?: Date): number => {
  if (!value) return 0;
  const ts = new Date(value).getTime();
  return Number.isFinite(ts) ? ts : 0;
};

export const computePurchaseUnitPriceFixes = (
  items: PurchaseItem[],
  options?: {
    maxBadUnitPrice?: number;
    minValidPrice?: number;
    resolveExternalPrice?: (item: PurchaseItem) => ExternalUnitPriceSource | null | undefined;
  },
): PurchaseUnitPriceFixResult => {
  const maxBadUnitPrice = options?.maxBadUnitPrice ?? 1;
  const minValidPrice = options?.minValidPrice ?? 0;
  const resolveExternalPrice = options?.resolveExternalPrice;

  const sorted = [...items].sort((a, b) => safeDateValue(b.date) - safeDateValue(a.date));
  const latestByKey = new Map<string, PurchaseItem>();
  const updates: PurchaseUnitPriceFixUpdate[] = [];
  let targetDocs = 0;
  const unmatchedCounter = new Map<string, PurchaseUnitPriceUnmatchedRow>();

  const recordUnmatched = (name: string, reason: PurchaseUnitPriceUnmatchedReason) => {
    const displayName = name || '(trống)';
    const key = `${displayName}__${reason}`;
    const existing = unmatchedCounter.get(key);
    if (existing) {
      existing.count += 1;
    } else {
      unmatchedCounter.set(key, { name: displayName, reason, count: 1 });
    }
  };

  sorted.forEach((item) => {
    const price = Number(item.unitPrice || 0);
    const isTarget = price <= maxBadUnitPrice;
    const isValidSource = price > maxBadUnitPrice && price > minValidPrice;

    const rawName = item.itemName || '';
    const key = buildMatchKey(rawName);
    const displayName = cleanupDisplayName(stripMonthToken(rawName)) || rawName || '';
    if (!key) {
      if (isTarget) {
        targetDocs += 1;
        recordUnmatched(displayName, 'missing_name');
      }
      return;
    }

    if (isValidSource) {
      if (!latestByKey.has(key)) {
        latestByKey.set(key, item);
      }
      return;
    }

    if (!isTarget) return;
    targetDocs += 1;

    let resolvedSource: ExternalUnitPriceSource | null = null;
    if (resolveExternalPrice) {
      resolvedSource = resolveExternalPrice(item) || null;
    }

    const source = resolvedSource || latestByKey.get(key);
    if (!source) {
      recordUnmatched(displayName, 'no_price_source');
      return;
    }

    const nextPrice = Number(source.unitPrice || 0);
    if (!Number.isFinite(nextPrice) || nextPrice <= maxBadUnitPrice) {
      recordUnmatched(displayName, 'no_price_source');
      return;
    }
    if (!item.id) return;

    const quantity = Number(item.quantity || 0);
    const sourceItemId = isExternalSource(source) ? source.sourceItemId : source.id;
    const sourceItemName = isExternalSource(source) ? source.sourceItemName : source.itemName;
    updates.push({
      id: item.id,
      unitPrice: nextPrice,
      total: roundNumber(quantity * nextPrice),
      sourceItemId,
      sourceItemName,
    });
  });

  const matchCounter = new Map<string, { name: string; unitPrice: number; count: number }>();
  updates.forEach((update) => {
    const name = update.sourceItemName || '-';
    const price = update.unitPrice;
    const key = `${name}__${price}`;
    const existing = matchCounter.get(key);
    if (existing) {
      existing.count += 1;
    } else {
      matchCounter.set(key, { name, unitPrice: price, count: 1 });
    }
  });

  const topMatches = Array.from(matchCounter.values())
    .sort((a, b) => b.count - a.count)
    .slice(0, 20);

  const unmatchedReasons = Array.from(unmatchedCounter.values())
    .sort((a, b) => b.count - a.count)
    .slice(0, 20);

  return {
    totalDocs: items.length,
    targetDocs,
    updatedDocs: updates.length,
    unmatchedDocs: Math.max(0, targetDocs - updates.length),
    updates,
    topMatches,
    unmatchedReasons,
  };
};
